import { assertRenderedBudget } from 'utils/shared/editorialRenderBudget';
import Markdoc, { type Config, type Node } from '@markdoc/markdoc';
function safeUrl(value: string, image = false) {
  if (
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\') &&
    !value.split('/').includes('..')
  )
    return true;
  try {
    const url = new URL(value);
    return (
      !url.username &&
      !url.password &&
      (image ? url.protocol === 'https:' : ['https:', 'http:', 'mailto:'].includes(url.protocol))
    );
  } catch {
    return false;
  }
}
export const grammar: Config = {
  tags: {
    image: {
      render: 'ContentImage',
      selfClosing: true,
      attributes: {
        asset: { type: String, required: true },
        src: { type: String, required: true },
        alt: { type: String, required: true },
        width: { type: Number, required: true },
        height: { type: Number, required: true },
        crop: { type: String },
        hotspot: { type: String },
      },
    },
    table: {
      render: 'ContentTable',
      selfClosing: true,
      attributes: {
        rows: { type: String, required: true },
        caption: { type: String },
        sourceType: { type: String, matches: ['table', 'tables'] },
      },
    },
    dataTable: {
      render: 'ContentTable',
      selfClosing: true,
      attributes: {
        rows: { type: String, required: true },
        caption: { type: String },
        sourceType: { type: String, matches: ['table', 'tables'] },
      },
    },
    newsletter: { render: 'NewsletterCta', selfClosing: true, attributes: {} },
    underline: { render: 'u', attributes: {}, inline: true },
    bold: { render: 'strong', attributes: {}, inline: true },
    italic: { render: 'em', attributes: {}, inline: true },
    sourceBold: { render: 'strong', attributes: {}, inline: true },
    sourceItalic: { render: 'em', attributes: {}, inline: true },
    sourceCenter: { render: 'SourceCenter', attributes: {}, inline: true },
    sourceHighlight: { render: 'SourceHighlight', attributes: {}, inline: true },
    sourceStrike: { render: 'SourceStrike', attributes: {}, inline: true },
    sourceBreak: { render: 'br', attributes: {}, inline: true, selfClosing: true },
    sourceEmptyParagraph: { render: 'p', attributes: {}, selfClosing: true },
    sourceLink: {
      render: 'a',
      inline: true,
      attributes: { href: { type: String, required: true } },
    },
    sourceInternalLink: {
      render: 'SourceInternalLink',
      inline: true,
      attributes: {
        slug: { type: String, required: true },
        reference: { type: String },
        weak: { type: Boolean },
      },
    },
  },
};
export function validateMarkdoc(body: string): Node {
  const tokens = new Markdoc.Tokenizer({
    html: true,
    allowLinkValidation: true,
  }).tokenize(body);
  function checkTokens(items: typeof tokens) {
    for (const token of items) {
      if (token.type === 'html_inline' || token.type === 'html_block')
        throw new Error('Raw HTML is prohibited');
      if (token.type === 'text' && /\]\(\s*(?:javascript|data|vbscript):/i.test(token.content))
        throw new Error('Unsafe link URL');
      if (token.children) checkTokens(token.children);
    }
  }
  checkTokens(tokens);
  const ast = Markdoc.parse(tokens);
  const issues = Markdoc.validate(ast, grammar);
  if (issues.length)
    throw new Error(`Invalid Markdoc: ${issues.map((issue) => issue.error.message).join('; ')}`);
  function checkAttributes(value: any) {
    if (!value || typeof value !== 'object') return;
    if (value.$$mdtype || value.type === 'variable' || value.type === 'function')
      throw new Error('Dynamic content is prohibited');
    Object.values(value).forEach(checkAttributes);
  }
  for (const node of ast.walk()) {
    checkAttributes(node.attributes);
    if (node.tag && !Object.hasOwn(grammar.tags!, node.tag))
      throw new Error(`Unreviewed tag ${node.tag}`);
    if (['class', 'className', 'style'].some((field) => Object.hasOwn(node.attributes, field)))
      throw new Error('Content styling is prohibited');
    if (
      (node.type === 'link' || node.tag === 'sourceLink') &&
      !safeUrl(String(node.attributes.href))
    )
      throw new Error('Unsafe link URL');
    if (
      node.tag === 'sourceInternalLink' &&
      (!/^[A-Za-z0-9][A-Za-z0-9._~-]*$/.test(node.attributes.slug) ||
        (node.attributes.reference &&
          !/^[A-Za-z0-9][A-Za-z0-9_.-]{0,240}$/.test(node.attributes.reference)) ||
        (node.attributes.weak !== undefined && !node.attributes.reference))
    )
      throw new Error('Invalid internal link');
    if (node.type === 'image')
      throw new Error('Use the reviewed image component with validated asset metadata');
    if (node.tag === 'image') {
      if (
        !safeUrl(node.attributes.src, true) ||
        !node.attributes.alt.trim() ||
        node.attributes.width <= 0 ||
        node.attributes.height <= 0
      )
        throw new Error('Invalid image');
      for (const field of ['crop', 'hotspot'])
        if (node.attributes[field]) {
          const value = JSON.parse(node.attributes[field]);
          if (
            !value ||
            typeof value !== 'object' ||
            Object.values(value).some((v) => typeof v !== 'number' || v < 0 || v > 1)
          )
            throw new Error(`Invalid ${field}`);
          if (
            field === 'crop' &&
            ((value.left || 0) + (value.right || 0) >= 1 ||
              (value.top || 0) + (value.bottom || 0) >= 1)
          )
            throw new Error('Invalid crop dimensions');
        }
    }
    if (node.tag === 'table' || node.tag === 'dataTable') {
      const rows = JSON.parse(node.attributes.rows);
      if (
        !Array.isArray(rows) ||
        !rows.length ||
        !rows[0].length ||
        rows.some(
          (row) =>
            !Array.isArray(row) ||
            row.length !== rows[0].length ||
            row.some((cell) => typeof cell !== 'string'),
        )
      )
        throw new Error('Invalid table rows');
    }
  }
  return ast;
}
export function renderTree(body: string) {
  const ast = validateMarkdoc(body);
  assertRenderedBudget([ast]);
  return Markdoc.transform(ast, grammar);
}
